<template>
    <modal-dialog
        v-if="createGoalCommand"
        ref="modal"
        label="Eigen doel toevoegen"
        description="Maak hier je Doel aan."
        :size="6"
        without-padding
        hide-close
    >
        <form-validation v-slot="{ bindings: formValidationBindings }">
            <flex-container
                :gap="Size.sm"
                :align-items="createGoalLoading ? 'center' : 'normal'"
                no-wrap
            >
                <flex-container
                    v-if="!createGoalLoading"
                    :size="5"
                    :gap="Size.xs"
                    :padding="Size.md"
                    direction="column"
                >
                    <text-heading sub>
                        ChatConnect
                    </text-heading>
                    <text-heading>
                        Eigen doel toevoegen
                    </text-heading>
                    <text-description>
                        Beschrijf het doel helder, inclusief de gewenste actie en informatie. De AI gaat hiermee
                        aan de slag en formuleert vervolgens een concreet doel en bepaalt welke gegevens nodig
                        zijn en verzameld moeten worden.
                    </text-description>

                    <flex-container :padding-y="Size.md">
                        <input-group
                            v-model="createGoalCommand.name"
                            :schema="string().required()"
                        >
                            <template #input>
                                <text-input placeholder="Naam van het doel" />
                            </template>
                        </input-group>
                        <input-group
                            v-model="createGoalCommand.recipientEmail"
                            :schema="string().email().required()"
                        >
                            <template #input>
                                <text-input placeholder="E-mailadres voor het ontvangen van resultaten" />
                            </template>
                        </input-group>
                        <input-group
                            v-model="createGoalCommand.goalDescription"
                            :schema="string().required()"
                        >
                            <template #input>
                                <text-area-input
                                    height="200px"
                                    placeholder="Beschrijf het doel en welke informatie verzameld moet worden."
                                />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container align-items="center" justify-content="flex-end">
                        <button-text @click="modal?.closeOverlay()">
                            Annuleren
                        </button-text>
                        <button-block :disabled="!formValidationBindings.isValid.value" @loading="createGoal($event)">
                            Doorgaan
                        </button-block>
                    </flex-container>
                </flex-container>

                <flex-container
                    v-else
                    :size="5"
                    height="full"
                    justify-content="center"
                >
                    <template v-if="!createGoalLoadingExpired">
                        <spinner-loader />
                        <flex-container
                            :gap="Size.xs"
                            align-content="center"
                            align-items="center"
                            direction="column"
                        >
                            <text-heading>
                                Moment geduld alstublieft.
                            </text-heading>
                            <text-description style="text-align: center">
                                We zijn het doel aan het aanmaken. <br>
                                Dit proces kan ongeveer 15 seconden duren.
                            </text-description>
                        </flex-container>
                    </template>
                    <template v-else>
                        <svg-icon
                            :icon="HexagonExclamationIcon"
                            :size="IconSize.xl"
                            color="primary"
                            is-custom-icon
                        />
                        <flex-container
                            :gap="Size.sm"
                            align-content="center"
                            align-items="center"
                            direction="column"
                        >
                            <text-heading>
                                Het duurt iets langer dan verwacht!
                            </text-heading>
                            <text-description style="text-align: center">
                                We werken eraan om het zo snel mogelijk te verwerken! <br>
                                Klik hieronder om terug te gaan naar het overzicht.
                            </text-description>
                            <button-outline @click="closeExpiredModal()">
                                Ga terug
                            </button-outline>
                        </flex-container>
                    </template>
                </flex-container>

                <flex-container
                    :size="5"
                    :color="'primary'"
                    :padding="Size.md"
                    :padding-top="Size.xl"
                >
                    <flex-container :gap="Size.xs" align-items="center">
                        <svg-icon
                            :icon="LightBulbIcon"
                            :color="'white'"
                            is-custom-icon
                        />
                        <text-heading :color="'white'">
                            Tips
                        </text-heading>
                    </flex-container>

                    <flex-container direction="column" :gap="Size.xs">
                        <text-decorator bold :color="'white'">
                            Naam van het doel
                        </text-decorator>
                        <text-description :color="'white'">
                            Geef de functie een duidelijke en korte naam.<br>
                            <i>Bijv.: "Proefrit aanvragen" of "Klantgegevens invullen"</i>
                        </text-description>
                    </flex-container>

                    <flex-container direction="column" :gap="Size.xs">
                        <text-decorator bold :color="'white'">
                            E-mailadres
                        </text-decorator>
                        <text-description :color="'white'">
                            Wie mag de AI Agent informeren bij dit doel?
                        </text-description>
                    </flex-container>

                    <flex-container direction="column" :gap="Size.xs">
                        <text-decorator bold :color="'white'">
                            Beschrijf wat de functie moet bereiken
                        </text-decorator>
                        <text-description :color="'white'">
                            <i>Bijv.: "Laat bezoekers een proefrit aanvragen met hun voorkeuren."</i><br>
                            Noem de velden die de gebruiker moet invullen.<br>
                            <i>Bijv.: "Naam, e-mail, telefoonnummer, voorkeursauto, datum en tijd."</i><br>
                            Hou het kort maar duidelijk.<br>
                            Geen lange zinnen, focus op kerninformatie.
                        </text-description>
                    </flex-container>
                </flex-container>
            </flex-container>
        </form-validation>
    </modal-dialog>
</template>

<script setup lang="ts">
    import { ButtonState, type ButtonStateChangedEvent, type ExposedOverlay, FlexContainer, IconSize, InputGroup, Size, TextAreaInput, TextDescription, TextHeading } from '@keyvue/one-ui';
    import { string } from 'yup';
    import { Assistent, ChatConnectGoal, ChatConnectGoalId, ChatConnectGoalStatus, CreateGoalCommand } from '@keyvue/one-api-client';
    import { onMounted, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import LightBulbIcon from '@keyvue/one-awesome-icons/icons/regular/lightbulb.svg?component';
    import HexagonExclamationIcon from '@keyvue/one-awesome-icons/icons/regular/hexagon-exclamation.svg?component';
    import { useRouter } from 'vue-router';

    const props = defineProps<{
        assistent: Assistent
    }>();

    const modal = ref<ExposedOverlay>();
    const createGoalCommand = ref<CreateGoalCommand>();
    const loadingTimeout = ref<null | number | NodeJS.Timeout>(null);
    const createGoalLoading = ref(false);
    const createGoalLoadingExpired = ref(false);

    const router = useRouter();
    const { client } = useClient();

    onMounted(async () => {
        setNewCreateGoalCommand();
    });

    function setNewCreateGoalCommand(): void {
        createGoalCommand.value = new CreateGoalCommand(
            ChatConnectGoalId.generate(),
            props.assistent.id,
            undefined,
            undefined,
            undefined,
        );
    }

    async function createGoal(event: ButtonStateChangedEvent): Promise<void> {
        if (createGoalCommand.value === undefined) {
            return;
        }

        createGoalLoading.value = true;

        await client.dispatch(createGoalCommand.value);

        try {
            await pollNewGoal();
        } catch {
            event.button.setState(ButtonState.completed);
            return;
        }

        createGoalLoading.value = false;
        event.button.setState(ButtonState.completed);
        modal.value?.closeOverlay();

        await router.push({
            name: 'assistent-chat-edit-chat-connect-goal',
            params: {
                goal: createGoalCommand.value.id.id,
            },
        });
    }

    async function pollNewGoal(tries: number = 0): Promise<void> {
        if (createGoalCommand.value === undefined) {
            return;
        }

        if (tries >= 15) {
            createGoalLoadingExpired.value = true;
            throw Error('Goal generation timeout');
        }

        await new Promise((resolve) => {
            if (loadingTimeout.value !== null) {
                clearTimeout(loadingTimeout.value);
            }

            loadingTimeout.value = setTimeout(resolve, 1000);
        });

        try {
            const createdGoal = await client.get(ChatConnectGoal, createGoalCommand.value.id);

            if (!createdGoal.status.equals(ChatConnectGoalStatus.READY)) {
                throw Error('Goal not ready');
            }
        } catch {
            await pollNewGoal(tries + 1);
        }
    }

    function closeExpiredModal() {
        createGoalLoading.value = false;
        createGoalLoadingExpired.value = false;
        modal.value?.closeOverlay();
    }

    defineExpose({
        openOverlay: () => {
            modal.value?.openOverlay();
        },
        closeOverlay: () => {
            modal.value?.closeOverlay();
        },
    });
</script>
